import { useEffect, useState } from "react";
import {
  getAdminInmates,
  getAdminInmate,
  createInmate,
  updateInmate,
  generateTempPin,
  getInmateResetRequests,
  importInmatesCsv,
} from "../api/adminInmates";
import AdminNav from "../components/AdminNav";
import AsyncButton from "../components/AsyncButton";

const blankForm = {
  inmate_number: "",
  first_name: "",
  last_name: "",
  housing_unit: "",
  status: "active",
};

export default function AdminInmates() {
  const [inmates, setInmates] = useState([]);
  const [resetRequests, setResetRequests] = useState([]);
  const [form, setForm] = useState(blankForm);
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [tempPinResult, setTempPinResult] = useState(null);
  const [selectedInmate, setSelectedInmate] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [csvFile, setCsvFile] = useState(null);
  const [csvResult, setCsvResult] = useState(null);
  const [csvLoading, setCsvLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [securityActionId, setSecurityActionId] = useState(null);

  const loadInmates = async () => {
    setLoading(true);

    try {
      const [inmateRes, resetRes] = await Promise.all([
        getAdminInmates(),
        getInmateResetRequests(),
      ]);
      setInmates(inmateRes.data.data || []);
      setResetRequests(resetRes.data.data || []);
    } catch (err) {
      alert("Failed to load inmates");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadInmates();
  }, []);

  const updateField = (field, value) => {
    setForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const resetForm = () => {
    setForm(blankForm);
    setEditingId(null);
  };

  const handleEdit = (inmate) => {
    setEditingId(inmate.id);
    setForm({
      inmate_number: inmate.inmate_number || "",
      first_name: inmate.first_name || "",
      last_name: inmate.last_name || "",
      housing_unit: inmate.housing_unit || "",
      status: inmate.status || "active",
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.inmate_number || !form.first_name || !form.last_name) {
      alert("Inmate number, first name, and last name are required.");
      return;
    }

    if (saving) return;
    setSaving(true);
    try {
      if (editingId) {
        await updateInmate(editingId, form);
      } else {
        await createInmate(form);
      }

      resetForm();
      await loadInmates();
    } catch (err) {
      alert(err.response?.data?.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };


  const handleCsvImport = async (e) => {
    e.preventDefault();

    if (!csvFile) {
      alert("Choose a CSV file first.");
      return;
    }

    setCsvLoading(true);
    setCsvResult(null);

    try {
      const res = await importInmatesCsv(csvFile);
      setCsvResult(res.data.data);
      setCsvFile(null);
      loadInmates();
    } catch (err) {
      alert(err.response?.data?.message || "CSV import failed");
    } finally {
      setCsvLoading(false);
    }
  };

  const handleViewDetails = async (inmate) => {
    setDetailLoading(true);

    try {
      const res = await getAdminInmate(inmate.id);
      setSelectedInmate(res.data.data);
    } catch (err) {
      alert(err.response?.data?.message || "Unable to load inmate details");
    } finally {
      setDetailLoading(false);
    }
  };

  const handleGenerateTempPin = async (inmate) => {
    const confirmed = await window.confirm(
     `Generate a temporary PIN for ${inmate.first_name} ${inmate.last_name}? Staff must verify identity before sharing it.`
    );

    if (!confirmed || securityActionId) return;
    setSecurityActionId(inmate.id);
    try {
      const res = await generateTempPin(inmate.id);
      setTempPinResult(res.data.data);
      loadInmates();
    } catch (err) {
      alert(err.response?.data?.message || "Unable to generate temporary PIN");
    } finally {
      setSecurityActionId(null);
    }
  };

  return (
    <div className="app-shell">
      <div className="topbar d-flex justify-content-between align-items-center">
        <div className="d-flex align-items-center gap-3">
          <div className="brand-mark">CV</div>
          <div>
            <h5 className="mb-0">CounselVault</h5>
            <div className="small opacity-75">Facility Inmate Management</div>
          </div>
        </div>
       <AdminNav />
      </div>

      <main className="page-wrap">
        <div className="d-flex justify-content-between align-items-end mb-4">
          <div>
            <h2 className="mb-1">Inmate Management</h2>
            <p className="text-muted mb-0">
              Create inmate records and issue temporary activation PINs after identity verification.
            </p>
          </div>

          <div className="stat-card px-4 py-3 text-end">
            <div className="text-muted small">Total Inmates</div>
            <h3 className="mb-0">{inmates.length}</h3>
          </div>
        </div>

        {tempPinResult && (
          <div className="alert alert-info border-0 shadow-sm mb-4">
            <div className="d-flex justify-content-between gap-3 align-items-start">
              <div>
                <h5 className="mb-1">Temporary PIN Generated</h5>
                <div>
                  Give this PIN to the inmate only after identity verification. It expires in 24 hours and must be changed at first login.
                </div>
                <div className="display-6 fw-bold mt-2 letter-spaced">{tempPinResult.temporary_pin}</div>
                <div className="small text-muted">Expires: {tempPinResult.expires_at}</div>
              </div>

              <button className="btn btn-sm btn-outline-dark" onClick={() => setTempPinResult(null)}>
                Dismiss
              </button>
            </div>
          </div>
        )}

        {selectedInmate && (
          <div className="portal-card mb-4">
            <div className="portal-card-header d-flex justify-content-between align-items-start gap-3">
              <div>
                <h5 className="mb-1">Inmate Control Center</h5>
                <div className="text-muted small">
                  Facility staff can verify identity details and issue reset PINs from this record.
                </div>
              </div>

              <button className="btn btn-sm btn-outline-secondary" onClick={() => setSelectedInmate(null)}>
                Close
              </button>
            </div>

            <div className="portal-card-body">
              <div className="row g-3 mb-4">
                <div className="col-md-3">
                  <div className="text-muted small">Facility Code</div>
                  <div className="fw-bold">{selectedInmate.facility_code || "-"}</div>
                </div>

                <div className="col-md-3">
                  <div className="text-muted small">Inmate Number</div>
                  <div className="fw-bold">{selectedInmate.inmate_number}</div>
                </div>

                <div className="col-md-3">
                  <div className="text-muted small">Status</div>
                  <div className="fw-bold text-capitalize">{selectedInmate.status}</div>
                </div>

                <div className="col-md-3">
                  <div className="text-muted small">Login State</div>
                  <div className="fw-bold">
                    {selectedInmate.login_enabled
                      ? selectedInmate.must_reset_password
                        ? "Temp PIN issued"
                        : "Active"
                      : "Not created"}
                  </div>
                </div>
              </div>

              <div className="row g-3 mb-4">
                <div className="col-md-6">
                  <div className="text-muted small">Name</div>
                  <div>{selectedInmate.first_name} {selectedInmate.last_name}</div>
                </div>

                <div className="col-md-6">
                  <div className="text-muted small">Housing Unit</div>
                  <div>{selectedInmate.housing_unit || "-"}</div>
                </div>

                <div className="col-md-6">
                  <div className="text-muted small">Last Password Reset</div>
                  <div>{selectedInmate.last_password_reset_at || "-"}</div>
                </div>

                <div className="col-md-6">
                  <div className="text-muted small">Temp PIN Expires</div>
                  <div>{selectedInmate.temp_pin_expires_at || "-"}</div>
                </div>
              </div>

              <div className="d-flex gap-2 mb-4">
                <button className="btn btn-dark" onClick={() => handleGenerateTempPin(selectedInmate)}>
                  {selectedInmate.login_enabled ? "Issue Reset PIN" : "Create Login PIN"}
                </button>

                <button className="btn btn-outline-dark" onClick={() => handleEdit(selectedInmate)}>
                  Edit Inmate
                </button>
              </div>

              <h6 className="fw-bold mb-2">Attorney Access</h6>

              {selectedInmate.attorney_accesses?.length ? (
                <div className="table-responsive">
                  <table className="table align-middle mb-0">
                    <thead>
                      <tr>
                        <th>Attorney</th>
                        <th>Law Firm</th>
                        <th>Status</th>
                        <th>Approved</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedInmate.attorney_accesses.map((access) => (
                        <tr key={access.id}>
                          <td>
                            <strong>{access.attorney_name || "-"}</strong>
                            <div className="text-muted small">{access.attorney_email || "-"}</div>
                          </td>
                          <td>{access.law_firm || "-"}</td>
                          <td>
                            <span className={`status-pill ${access.status === "approved" ? "status-released" : access.status === "pending" ? "status-approved" : "status-rejected"}`}>
                              {access.status}
                            </span>
                          </td>
                          <td>{access.approved_at || "-"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-muted small">No attorney access records for this inmate yet.</div>
              )}
            </div>
          </div>
        )}

        {resetRequests.length > 0 && (
          <div className="portal-card mb-4">
            <div className="portal-card-header">
              <h5 className="mb-0">Pending Inmate Access Reset Requests</h5>
              <div className="text-muted small">Verify identity in person before generating a temporary PIN.</div>
            </div>
            <div className="portal-card-body">
              <div className="table-responsive">
                <table className="table align-middle mb-0">
                  <thead>
                    <tr>
                      <th>Facility Code</th>
                      <th>Inmate Number</th>
                      <th>Matched Inmate</th>
                      <th>Requested</th>
                      <th className="text-end">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {resetRequests.map((req) => (
                      <tr key={req.id}>
                        <td>{req.facility_code_submitted || "-"}</td>
                        <td>{req.inmate_number || "-"}</td>
                        <td>
                          {req.inmate ? `${req.inmate.first_name} ${req.inmate.last_name}` : "Needs staff lookup"}
                        </td>
                        <td>{req.created_at || "-"}</td>
                        <td className="text-end">
                          {req.inmate && (
                            <button
                              className="btn btn-sm btn-dark"
                              onClick={() => handleGenerateTempPin(req.inmate)}
                            >
                              Generate PIN
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        <div className="row g-4">
          <div className="col-lg-5">
            <div className="portal-card">
              <div className="portal-card-header">
                <h5 className="mb-0">{editingId ? "Edit Inmate" : "Add Inmate"}</h5>
                <div className="text-muted small">Inmates are scoped to your facility.</div>
              </div>

              <div className="portal-card-body">
                <form onSubmit={handleSubmit}>
                  <div className="mb-3">
                    <label className="form-label">Inmate Number</label>
                    <input className="form-control" value={form.inmate_number} onChange={(e) => updateField("inmate_number", e.target.value)} />
                  </div>

                  <div className="row">
                    <div className="col-md-6 mb-3">
                      <label className="form-label">First Name</label>
                      <input className="form-control" value={form.first_name} onChange={(e) => updateField("first_name", e.target.value)} />
                    </div>

                    <div className="col-md-6 mb-3">
                      <label className="form-label">Last Name</label>
                      <input className="form-control" value={form.last_name} onChange={(e) => updateField("last_name", e.target.value)} />
                    </div>
                  </div>

                  <div className="mb-3">
                    <label className="form-label">Housing Unit</label>
                    <input className="form-control" value={form.housing_unit} onChange={(e) => updateField("housing_unit", e.target.value)} placeholder="Optional" />
                  </div>

                  <div className="mb-4">
                    <label className="form-label">Status</label>
                    <select className="form-select" value={form.status} onChange={(e) => updateField("status", e.target.value)}>
                      <option value="active">Active</option>
                      <option value="inactive">Inactive</option>
                    </select>
                  </div>

                  <div className="d-flex gap-2">
                    <AsyncButton className="btn btn-dark flex-grow-1" busy={saving} busyText="Saving…">{editingId ? "Save Changes" : "Create Inmate"}</AsyncButton>
                    {editingId && (
                      <button type="button" className="btn btn-outline-secondary" onClick={resetForm}>Cancel</button>
                    )}
                  </div>
                </form>
              </div>
            </div>

            <div className="portal-card mt-4">
              <div className="portal-card-header">
                <h5 className="mb-0">CSV Import</h5>
                <div className="text-muted small">Upload inmate_number, first_name, last_name, housing_unit, status.</div>
              </div>

              <div className="portal-card-body">
                <form onSubmit={handleCsvImport}>
                  <div className="mb-3">
                    <input
                      type="file"
                      className="form-control"
                      accept=".csv,text/csv"
                      onChange={(e) => setCsvFile(e.target.files?.[0] || null)}
                    />
                  </div>

                  <button className="btn btn-outline-dark w-100" disabled={csvLoading}>
                    {csvLoading ? "Importing..." : "Import CSV"}
                  </button>
                </form>

                {csvResult && (
                  <div className="alert alert-success mt-3 mb-0 py-2">
                    Created {csvResult.created}, updated {csvResult.updated}, skipped {csvResult.skipped}.
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="col-lg-7">
            <div className="portal-card">
              <div className="portal-card-header d-flex justify-content-between align-items-center">
                <div>
                  <h5 className="mb-0">Facility Inmates</h5>
                  <div className="text-muted small">Attorneys can upload documents for active inmates with approved access.</div>
                </div>
                <button className="btn btn-sm btn-outline-secondary" onClick={loadInmates}>Refresh</button>
              </div>

              <div className="portal-card-body">
                {loading ? (
                  <p className="mb-0">Loading inmates...</p>
                ) : inmates.length === 0 ? (
                  <div className="text-center py-5">
                    <h5>No inmates yet</h5>
                    <p className="text-muted mb-0">Add your first inmate to enable attorney uploads.</p>
                  </div>
                ) : (
                  <div className="table-responsive">
                    <table className="table align-middle mb-0">
                      <thead>
                        <tr>
                          <th>Inmate</th>
                          <th>Number</th>
                          <th>Housing</th>
                          <th>Status</th>
                          <th>Login</th>
                          <th className="text-end">Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {inmates.map((inmate) => (
                          <tr key={inmate.id}>
                            <td>
                              <strong>{inmate.first_name} {inmate.last_name}</strong>
                              <div className="text-muted small">ID #{inmate.id}</div>
                            </td>
                            <td>{inmate.inmate_number}</td>
                            <td>{inmate.housing_unit || "-"}</td>
                            <td>
                              <span className={`status-pill ${inmate.status === "active" ? "status-released" : "status-rejected"}`}>
                                {inmate.status}
                              </span>
                            </td>
                            <td>
                              {inmate.login_enabled ? (
                                <span className={`status-pill ${inmate.must_reset_password ? "status-approved" : "status-released"}`}>
                                  {inmate.must_reset_password ? "Temp PIN" : "Active"}
                                </span>
                              ) : (
                                <span className="text-muted small">Not created</span>
                              )}
                            </td>
                            <td className="text-end">
                              <div className="d-flex gap-2 justify-content-end">
                                <button className="btn btn-sm btn-outline-secondary" onClick={() => handleViewDetails(inmate)} disabled={detailLoading}>Details</button>
                                <button className="btn btn-sm btn-outline-dark" onClick={() => handleEdit(inmate)}>Edit</button>
                                <AsyncButton className="btn btn-sm btn-dark" busy={securityActionId === inmate.id} busyText="Generating…" onClick={() => handleGenerateTempPin(inmate)}>
                                  {inmate.login_enabled ? "Reset PIN" : "Create Login"}
                                </AsyncButton>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
